import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import FullLogo from 'assets/images/logos/FullLogo.png';
import FitLogo from 'assets/images/logos/FitLogo.png';

import ROUTES from 'utils/routes';
import { backendFunction } from 'utils/backendApi';
import { useUser } from 'utils/userContext';
import {
    MONTHLY_SUBSCRIPTION,
    getPostCheckoutDestination,
    sanitizeInternalRedirectPath,
} from 'utils/subscription';

const MAX_SYNC_ATTEMPTS = 8;
const SYNC_RETRY_DELAY_MS = 1500;

function SuccessBadge() {
    return (
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary bg-[color:color-mix(in_srgb,var(--primary)_18%,transparent)]">
            <svg className="h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                    d="M6 12.5l4 4 8-9"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );
}

function SubscriptionSuccess() {
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = React.useMemo(
        () => sanitizeInternalRedirectPath(new URLSearchParams(location.search).get('redirectTo')),
        [location.search]
    );

    const {
        user,
        reloadUser,
        hasPaidAccess,
    } = useUser();

    const [syncState, setSyncState] = React.useState(hasPaidAccess ? 'success' : 'syncing');
    const [message, setMessage] = React.useState(
        hasPaidAccess ? '' : 'We are confirming your payment with Kodeo now.'
    );
    const [portalLoading, setPortalLoading] = React.useState(false);
    const hasPaidAccessRef = React.useRef(hasPaidAccess);

    React.useEffect(() => {
        hasPaidAccessRef.current = hasPaidAccess;
    }, [hasPaidAccess]);

    React.useEffect(() => {
        if (hasPaidAccess) {
            setSyncState('success');
            setMessage('');
        }
    }, [hasPaidAccess]);

    React.useEffect(() => {
        if (hasPaidAccess) return;

        let cancelled = false;

        async function syncSubscriptionStatus() {
            setSyncState('syncing');
            setMessage('We are confirming your payment with Kodeo now.');

            for (let attempt = 0; attempt < MAX_SYNC_ATTEMPTS; attempt++) {
                await reloadUser(false);

                if (cancelled) return;

                if (attempt < MAX_SYNC_ATTEMPTS - 1) {
                    await new Promise(resolve => setTimeout(resolve, SYNC_RETRY_DELAY_MS));
                }
            }

            if (!cancelled) {
                setSyncState('timeout');
                setMessage('Stripe finished successfully, but Kodeo is still syncing your access. Please wait a few seconds and try again.');
            }
        }

        syncSubscriptionStatus();

        return () => {
            cancelled = true;
        };
    }, [hasPaidAccess, reloadUser]);

    const openBillingPortal = async () => {
        setPortalLoading(true);
        setMessage('');

        const { data, error } = await backendFunction('create-customer-portal-session', {
            method: 'POST',
            withAuth: true,
        });

        if (error || !data?.url) {
            setMessage(error || 'Unable to open billing settings right now.');
            setPortalLoading(false);
            return;
        }

        window.location.assign(data.url);
    };

    const handleContinue = () => {
        navigate(getPostCheckoutDestination(user, redirectTo, ROUTES.LESSONS));
    };

    const handleRetry = async () => {
        setSyncState('syncing');
        setMessage('Trying your access sync again...');
        await reloadUser(false);
        if (!hasPaidAccessRef.current) {
            setSyncState('timeout');
            setMessage('Access is still syncing. Please refresh this page in a few seconds if it does not update.');
        }
    };

    return (
        <div
            className="min-h-screen flex flex-col items-center bg-background px-4 break-small:px-20 break-big:px-40"
            style={{ background: 'linear-gradient(to bottom, var(--background) 0%, var(--background) 18%, var(--primary) 700%)' }}
        >
            {portalLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className='flex items-center justify-center flex-col'>
                        <div className="loader relative">
                            <div className="spinner w-28 h-28 rounded-full animate-spin border-t-[6px] border-b-[6px] border-white" />
                            <img src={FitLogo} alt="Loading..." className='absolute inset-0 m-auto p-4' />
                        </div>
                        <p className="text-center text-white p-4 font-bold">Opening Stripe...</p>
                    </div>
                </div>
            )}

            <nav className="w-full flex items-center justify-between py-5 z-10">
                <Link to={ROUTES.LANDING}><img src={FullLogo} alt="Kodeo Logo" className="h-6 w-auto" /></Link>
                <div className="flex gap-4 items-center">
                    <Link to={ROUTES.SETTINGS + '/subscription'} className="bg-background-light text-white font-bold px-6 py-2 rounded-2xl shadow-md hover:bg-background transition-all duration-500 text-lg border-2 border-primary">
                        Settings
                    </Link>
                </div>
            </nav>

            <div className="w-full max-w-[980px] flex flex-col gap-6 py-8 md:py-14">
                <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
                    <section className="rounded-[2rem] border-2 border-border bg-background-light/90 p-7 md:p-10 shadow-xl shadow-black/20">
                        <p className="text-primary font-bold uppercase tracking-[0.24em] text-sm">Membership</p>
                        <h1 className="text-white text-4xl md:text-5xl font-extrabold leading-tight mt-3">
                            {syncState === 'success' ? 'Payment successful.' : 'Confirming your payment...'}
                        </h1>
                        <p className="text-text-light text-lg mt-4 max-w-[42rem]">
                            {syncState === 'success'
                                ? 'Stripe confirmed your payment and Kodeo has unlocked your membership.'
                                : 'Your checkout completed in Stripe. We are now waiting for Kodeo to finish syncing your access.'}
                        </p>

                        <div className="grid gap-3 sm:grid-cols-2 mt-8">
                            <div className="rounded-2xl border border-border p-4">
                                <h2 className="font-bold text-xl">What happens next</h2>
                                <p className="text-text-light mt-2">Once access is ready, continue into the roadmap and start learning right away.</p>
                            </div>
                            <div className="rounded-2xl border border-border p-4">
                                <h2 className="font-bold text-xl">Billing stays simple</h2>
                                <p className="text-text-light mt-2">You can always manage your plan, payment method, and cancellations from Stripe billing.</p>
                            </div>
                        </div>
                    </section>

                    <aside className="rounded-[2rem] border-2 border-primary bg-[color:color-mix(in_srgb,var(--primary)_16%,var(--background))] p-7 md:p-8 shadow-xl shadow-primary/20">
                        <p className="text-text-light font-bold uppercase tracking-[0.24em] text-sm">One plan</p>
                        <h2 className="text-white text-3xl font-extrabold mt-3">{MONTHLY_SUBSCRIPTION.name}</h2>
                        <div className="text-white text-5xl font-black leading-none mt-5">{MONTHLY_SUBSCRIPTION.priceDisplay}</div>
                        <p className="text-text-light mt-3">{MONTHLY_SUBSCRIPTION.tagline}</p>

                        <div className="mt-6 rounded-2xl border border-border bg-background/60 p-5">
                            <div className="flex items-center gap-4">
                                <SuccessBadge />
                                <div>
                                    <div className="text-text-light text-sm">Current status</div>
                                    <div className="font-bold text-white">
                                        {syncState === 'success' ? 'Access unlocked' : syncState === 'timeout' ? 'Still syncing' : 'Confirming payment'}
                                    </div>
                                </div>
                            </div>

                            {message && (
                                <p className="text-sm text-text-light mt-4">
                                    {message}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-3 mt-6">
                            {syncState === 'success' ? (
                                <button
                                    onClick={handleContinue}
                                    className="w-full bg-primary text-background font-bold py-4 rounded-2xl shadow-sm shadow-primary text-xl border-2 border-primary"
                                >
                                    Continue learning
                                </button>
                            ) : (
                                <button
                                    onClick={handleRetry}
                                    className="w-full bg-primary text-background font-bold py-4 rounded-2xl shadow-sm shadow-primary text-xl border-2 border-primary"
                                >
                                    Check access again
                                </button>
                            )}

                            {Boolean(user?.stripe_customer_id) && (
                                <button
                                    onClick={openBillingPortal}
                                    disabled={portalLoading}
                                    className="w-full bg-transparent text-text font-bold py-4 rounded-2xl border-2 border-border hover:bg-background-pop transition-colors duration-300 disabled:opacity-60"
                                >
                                    Manage billing
                                </button>
                            )}

                            <Link
                                to={ROUTES.SUBSCRIPTION}
                                className="w-full text-center bg-transparent text-text-light font-bold py-3 rounded-2xl border-2 border-border hover:bg-background-pop transition-colors duration-300"
                            >
                                Back to membership
                            </Link>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

export default SubscriptionSuccess;
